import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import ListingDocuments from './ListingDocuments';

const STATUS_CONFIG = {
  published: { label: 'En ligne',    bg: '#E8F5F0', color: '#0F6E56' },
  pending:   { label: 'En attente',  bg: '#FDF0E6', color: '#C4784A' },
  suspended: { label: 'Suspendue',   bg: '#FCEBEB', color: '#A32D2D' },
  draft:     { label: 'Brouillon',   bg: '#F0F0F0', color: '#6B6B6B' },
};

export default function MyListingsPage({ onNavigate }) {
  const [user, setUser]               = useState(null);
  const [profile, setProfile]         = useState(null);
  const [listings, setListings]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [bio, setBio]                 = useState('');
  const [bioSaving, setBioSaving]     = useState(false);
  const [bioSaved, setBioSaved]       = useState(false);
  const [isMobile, setIsMobile]       = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const loadData = useCallback(async (uid) => {
    try {
      const [{ data: prof }, { data: lstgs }] = await Promise.all([
        supabase.from('host_profiles').select('*').eq('id', uid).single(),
        supabase.from('listings').select('*').eq('host_id', uid).order('created_at', { ascending: false }),
      ]);
      if (prof) { setProfile(prof); setBio(prof.bio || ''); }
      setListings(lstgs || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) { setUser(session.user); loadData(session.user.id); }
      else { setLoading(false); onNavigate('auth'); }
    });
  }, [loadData, onNavigate]);

  const saveBio = async () => {
    setBioSaving(true);
    await supabase.from('host_profiles').update({ bio }).eq('id', user.id);
    setBioSaving(false);
    setBioSaved(true);
    setTimeout(() => setBioSaved(false), 2500);
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ fontSize: 14, color: 'var(--ink-3)' }}>Chargement...</div>
    </div>
  );

  const firstName = profile?.full_name?.split(' ')[0] || 'hôte';
  const drafts = listings.filter(l => l.status === 'draft');
  const published = listings.filter(l => l.status !== 'draft');

  return (
    <div style={{ background: 'var(--surface)', minHeight: '100vh', paddingTop: 60 }}>
      <div style={{ maxWidth: 860, margin: '0 auto', padding: isMobile ? '1.5rem 1rem' : '2.5rem 2rem' }}>

        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <button onClick={() => onNavigate('dashboard')} style={{ fontSize: 13, color: 'var(--ink-3)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--sans)', padding: 0, marginBottom: 12 }}>← Retour au dashboard</button>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h1 style={{ fontFamily: 'var(--serif)', fontSize: 28, letterSpacing: '-0.5px', marginBottom: 4 }}>Mes annonces</h1>
              <p style={{ fontSize: 14, color: 'var(--ink-3)' }}>{published.length} annonce{published.length !== 1 ? 's' : ''}{drafts.length > 0 ? ` · ${drafts.length} brouillon${drafts.length > 1 ? 's' : ''}` : ''}</p>
            </div>
            <button onClick={() => onNavigate('newlisting')}
              style={{ padding: '10px 20px', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 24, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)', whiteSpace: 'nowrap' }}>
              + Nouvelle annonce
            </button>
          </div>
        </div>

        {/* Toast bio saved */}
        {bioSaved && (
          <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: 'var(--ink)', color: '#fff', padding: '10px 20px', borderRadius: 24, fontSize: 13, fontWeight: 500, zIndex: 9999 }}>
            + Biographie enregistree
          </div>
        )}

        {/* ── SECTION BIO ── */}
        <div style={{ background: 'var(--white)', borderRadius: 16, border: '1px solid var(--border)', padding: '24px', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 600, color: '#fff', flexShrink: 0 }}>
              {firstName[0]?.toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 500 }}>{profile?.full_name || 'Mon profil'}</div>
              <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>hôte Havenly depuis {profile?.created_at ? new Date(profile.created_at).getFullYear() : '2025'}</div>
            </div>
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-3)', display: 'block', marginBottom: 6 }}>
              A propos de moi <span style={{ fontWeight: 400, color: 'var(--ink-4)' }}>— visible par vos voyageurs</span>
            </label>
            <textarea
              value={bio}
              onChange={e => setBio(e.target.value)}
              placeholder={`Bonjour, je m'appelle ${firstName} ! Parlez de vous, de votre région, de ce que vous aimez partager avec vos voyageurs...`}
              maxLength={500}
              style={{ width: '100%', padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 10, fontSize: 14, fontFamily: 'var(--sans)', resize: 'vertical', minHeight: 110, outline: 'none', boxSizing: 'border-box', lineHeight: 1.6 }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>{bio.length}/500 caracteres</span>
              <button onClick={saveBio} disabled={bioSaving}
                style={{ padding: '8px 20px', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 20, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
                {bioSaving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>

        {/* ── BROUILLONS ── */}
        {drafts.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-3)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ background: '#F0F0F0', color: '#6B6B6B', fontSize: 11, padding: '2px 10px', borderRadius: 20, fontWeight: 500 }}>Brouillons</span>
              {drafts.length} brouillon{drafts.length > 1 ? 's' : ''} en cours
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {drafts.map(l => (
                <div key={l.id} style={{ background: 'var(--white)', borderRadius: 16, border: '2px dashed var(--border)', overflow: 'hidden', opacity: 0.9 }}>
                  <div style={{ display: 'flex', gap: 0, flexDirection: isMobile ? 'column' : 'row' }}>
                    <div style={{ width: isMobile ? '100%' : 200, height: isMobile ? 180 : 'auto', flexShrink: 0, position: 'relative' }}>
                      {l.photos?.[0] ? (
                        <img src={l.photos[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', minHeight: 160, display: 'block' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', minHeight: 160, background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40 }}>📝</div>
                      )}
                      <span style={{ position: 'absolute', top: 10, left: 10, fontSize: 11, padding: '3px 10px', borderRadius: 20, background: '#F0F0F0', color: '#6B6B6B', fontWeight: 500 }}>
                        Brouillon
                      </span>
                    </div>
                    <div style={{ flex: 1, padding: '20px' }}>
                      <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 4 }}>{l.title || 'Brouillon sans titre'}</div>
                      <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 8 }}>{l.type || '—'} · {l.city || '—'}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink-4)', marginBottom: 16 }}>
                        Dernière modification : {new Date(l.updated_at).toLocaleDateString('fr-FR')}
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button onClick={() => onNavigate('newlisting', l)}
                          style={{ padding: '8px 18px', background: '#534AB7', color: '#fff', border: 'none', borderRadius: 20, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
                          Continuer la création
                        </button>
                        <button onClick={async () => {
                          if (!window.confirm('Supprimer ce brouillon ?')) return;
                          await supabase.from('listings').delete().eq('id', l.id);
                          setListings(prev => prev.filter(x => x.id !== l.id));
                        }}
                          style={{ padding: '8px 18px', background: 'transparent', color: '#A32D2D', border: '1px solid #F09595', borderRadius: 20, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
                          Supprimer
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── LISTE ANNONCES ── */}
        {published.length === 0 ? (
          <div style={{ background: 'var(--white)', borderRadius: 16, border: '1px solid var(--border)', padding: '3rem', textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🏠</div>
            <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 8 }}>Vous n'avez pas encore d'annonce</div>
            <div style={{ fontSize: 14, color: 'var(--ink-3)', marginBottom: 20 }}>Publiez votre premier logement et commencez à accueillir des voyageurs.</div>
            <button onClick={() => onNavigate('newlisting')}
              style={{ padding: '11px 28px', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 24, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
              Créer mon annonce
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {published.map(l => {
              const status = STATUS_CONFIG[l.status] || STATUS_CONFIG.pending;
              return (
                <div key={l.id} style={{ background: 'var(--white)', borderRadius: 16, border: '1px solid var(--border)', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', gap: 0, flexDirection: isMobile ? 'column' : 'row' }}>
                    <div style={{ width: isMobile ? '100%' : 200, height: isMobile ? 180 : 'auto', flexShrink: 0, position: 'relative' }}>
                      {l.photos?.[0] ? (
                        <img src={l.photos[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', minHeight: 160, display: 'block' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', minHeight: 160, background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40 }}>🏠</div>
                      )}
                      <span style={{ position: 'absolute', top: 10, left: 10, fontSize: 11, padding: '3px 10px', borderRadius: 20, background: status.bg, color: status.color, fontWeight: 500 }}>
                        {status.label}
                      </span>
                    </div>
                    <div style={{ flex: 1, padding: '20px 20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
                        <div>
                          <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 4 }}>{l.title || '(Sans titre)'}</div>
                          <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{l.type || '—'} · {l.city || '—'}</div>
                        </div>
                        {l.price_night && (
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontSize: 18, fontWeight: 500 }}>{l.price_night} €</div>
                            <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>/ nuit</div>
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
                        {[
                          { label: 'Voyageurs max', val: l.guests_max || '—' },
                          { label: 'Chambres', val: l.bedrooms || '—' },
                          { label: 'Score', val: `${l.score_transparency || 0}/100` },
                          { label: 'N° enreg.', val: l.registration_number ? '✓' : '⚠ manquant', warn: !l.registration_number },
                        ].map(s => (
                          <div key={s.label} style={{ fontSize: 12 }}>
                            <span style={{ color: 'var(--ink-3)' }}>{s.label} : </span>
                            <span style={{ fontWeight: 500, color: s.warn ? '#A32D2D' : 'var(--ink)' }}>{s.val}</span>
                          </div>
                        ))}
                      </div>
                      {l.status === 'pending' && (
                        <div style={{ background: '#FDF0E6', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#C4784A', marginBottom: 14 }}>
                          Votre annonce est en cours de validation par l'équipe Havenly (sous 24h).
                        </div>
                      )}
                      {l.status === 'suspended' && (
                        <div style={{ background: '#FCEBEB', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#A32D2D', marginBottom: 14 }}>
                          Votre annonce a été suspendue. Contactez-nous à contact@havenly.fr pour plus d'informations.
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button onClick={() => onNavigate('newlisting', l)}
                          style={{ padding: '8px 18px', background: 'var(--ink)', color: '#fff', border: 'none', borderRadius: 20, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
                          Modifier
                        </button>
                        {l.status === 'published' && (
                          <button onClick={() => onNavigate('listing', l)}
                            style={{ padding: '8px 18px', background: 'transparent', color: 'var(--ink)', border: '1px solid var(--border)', borderRadius: 20, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
                            Voir l'annonce
                          </button>
                        )}
                        <button onClick={async () => {
                          const today = new Date().toISOString().split('T')[0];
                          const { data: activeBookings } = await supabase
                            .from('bookings')
                            .select('id')
                            .eq('listing_id', l.id)
                            .in('status', ['confirmed', 'pending'])
                            .gte('date_end', today);
                          if (activeBookings?.length > 0) {
                            const force = window.confirm(
                              `Cette annonce a ${activeBookings.length} reservation(s) active(s).\n\nCliquez OK pour les annuler automatiquement et supprimer l'annonce.\nCliquez Annuler pour abandonner.`
                            );
                            if (!force) return;
                            for (const b of activeBookings) {
                              try {
                                await fetch('/api/cancel-booking', {
                                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ bookingId: b.id, cancelledBy: 'host' }),
                                });
                              } catch (e) {
                                await supabase.from('bookings').update({ status: 'cancelled' }).eq('id', b.id);
                              }
                            }
                          } else if (!window.confirm(`Supprimer definitivement "${l.title || 'cette annonce'}" ? Cette action est irreversible.`)) {
                            return;
                          }
                          await supabase.from('listings').delete().eq('id', l.id);
                          setListings(prev => prev.filter(x => x.id !== l.id));
                        }}
                          style={{ padding: '8px 18px', background: 'transparent', color: '#A32D2D', border: '1px solid #F09595', borderRadius: 20, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
                          Supprimer
                        </button>
                      </div>
                    </div>
                  </div>
                  <div style={{ borderTop: '1px solid var(--border)', padding: '16px 20px' }}>
                    <ListingDocuments listing={l} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
